import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from '@mui/material/Divider';
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import CommentTags from '../TemplateUtils/CommentTags';
import FilterTags from '../../components/CommentLibrary/FilterTags';
import CommentSearch from '../../components/CommentLibrary/CommentSearch';
import { CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';

function GlobalListOptions(props) {
    const {
        getGlobalLists,
        globalLists,
        isLoading,
        hasMore,
        currentPage,
        filterTagList,
        selectedFilterTagID,
        setSelectedFilterTagID,
        searchData,
        setSearchData,
        setState,
        addDropdown
    } = props;
    const storeData = useSelector((state) => state.CommonReducer);

    const loadingRef = useRef(null);
    const stickyHeaderRef = useRef(null);
    const observerRef = useRef(null);
    const pageNumRef = useRef(currentPage || 1);
    const isLoadingRef = useRef(false);
    const location = useLocation();

    const resetLoadingRef = useCallback(() => {
        isLoadingRef.current = false;
    }, []);

    React.useImperativeHandle(props.forwardedRef, () => ({
        resetLoadingRef
    }));

    const handleSearchFilter = async (value) => {
        setSearchData(value);
        isLoadingRef.current = false;
        await getGlobalLists(0, value, location.pathname.includes("templates") ? selectedFilterTagID : []);
    };

    const handleSelectItem = async (event, item) => {
        event.stopPropagation();
        await addDropdown(event, item);
        setState(prevState => ({ ...prevState, open: false }));
        setSearchData(null);
        setSelectedFilterTagID(null);
    }

    useEffect(() => {
        pageNumRef.current = currentPage || 1;
    }, [currentPage]);

    useEffect(() => {
        if (!isLoading) {
            isLoadingRef.current = false;
        }
    }, [isLoading]);

    useEffect(() => {
        if (isLoading || !hasMore) return;
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting && !isLoadingRef.current && hasMore) {
                    isLoadingRef.current = true;
                    const nextPage = pageNumRef.current + 1;
                    getGlobalLists(nextPage, searchData, location.pathname.includes("templates") ? selectedFilterTagID : []);
                }
            },
            { root: null, rootMargin: "100px", threshold: 0.1 }
        );
        if (loadingRef.current) observerRef.current.observe(loadingRef.current);
        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [hasMore, isLoading, getGlobalLists, searchData, selectedFilterTagID]);

    return (
        <Grid container style={{ position: 'relative' }}>
            <Grid
                ref={stickyHeaderRef}
                item
                xs={12}
                sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '16px 0' }}
            >
                <Grid container spacing={2} sx={{ padding: '0 16px' }}>
                    <Grid item xs={5.95}>
                        <FilterTags
                            filterTagList={filterTagList}
                            selectedFilterTagID={location.pathname.includes("templates") ? selectedFilterTagID : []}
                            setSelectedFilterTagID={setSelectedFilterTagID}
                            searchInput={searchData}
                            component={'global_lists'}
                            defaultCategories={location.pathname.includes("templates") ? storeData?.defaultCategories : []}
                        />
                    </Grid>
                    <Grid item xs={5.95}>
                        <div className="cl-search" style={{ position: 'relative' }}>
                            <CommentSearch onSearch={(val) => handleSearchFilter(val)} value={searchData} />
                            <SearchIcon className="cl-search-icon" />
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item container xs={12} sx={{ overflowY: 'auto', height: 'calc(100% - 70px)', paddingTop: "10px !important" }}>
                {globalLists?.length > 0 ? globalLists?.map((list, index) => (
                    <Box sx={{ cursor: 'pointer', width: "100%" }} key={`list-${index}`} onClick={(e) => handleSelectItem(e, list)}>
                        <Grid item display={"flex"} xs={12} mt={0.7} mb={0.7} width={"100%"} >
                            {list?.name && !(/^dropdown\s+[a-z0-9-]+$/i.test(list?.name)) ? <Grid item display={"flex"} xs={2} ml={3.2} mt={1}>
                                <Typography className={list?.name ? "dropdown_name" : 'untitled_text'}>
                                    {/^dropdown\s+[a-z0-9-]+$/i.test(list?.name) ? "" : list?.name || ''}
                                </Typography>
                            </Grid> : null}
                            <Grid item container display={"flex"} xs={list?.name ? 9.6 : 12} mx={2} mb={1} ml={3.2} >
                                <Grid item xs={12} display={"flex"} mt={1} flexWrap="wrap" alignItems="center">
                                    {list?.options?.map((item, i) => (
                                        <React.Fragment key={`item-${i}`}>
                                            <Typography className='options_text'>{item}</Typography>
                                            {i < list.options.length - 1 && (
                                                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: "#d1d1d1ff !important" }} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Grid>
                                <Grid item xs={12}>
                                    <CommentTags tags={list?.tags} selectedFilterTagID={selectedFilterTagID} />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Divider sx={{ width: "100%", borderColor: "color/neutral/neutral-300 !important" }} />
                    </Box>
                )) : !isLoading && (
                    <Grid container justifyContent="center" sx={{ padding: 4 }}>
                        <Typography>No global lists found</Typography>
                    </Grid>
                )}
                {hasMore && (
                    <div
                        ref={loadingRef}
                        style={{ height: "100%", width: "100%", margin: "20px 0", textAlign: "center", alignItems: "center", display: "flex", justifyContent: "center" }}
                        aria-label="Loading more"
                    >
                        {isLoading && <CircularProgress />}
                    </div>
                )}
                {!hasMore && globalLists?.length > 0 && (
                    <Grid container display={"flex"} justifyContent="center" alignItems={"flex-end"} sx={{ padding: 1 }} >
                        <Typography>All lists loaded</Typography>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}

export default React.forwardRef((props, ref) => (
    <GlobalListOptions {...props} forwardedRef={ref} />
));